/**
 * Cart drawer on the real WooCommerce cart (port of redesign/assets/cart.js).
 *
 * - Opens from any [data-open-cart] link (which still points to /cart/ without JS)
 *   and automatically after an add to cart (tc:cart:added from woo-bridge.js).
 * - Quantity and remove buttons post to ?wc-ajax=tc_cart_set_qty / tc_cart_remove;
 *   responses are WooCommerce fragment payloads applied in place.
 * - Fragments are written to WooCommerce's session storage so cart-fragments.js
 *   stays in sync across page loads.
 */
const TC = window.TC || {};
const drawer = document.getElementById('cart-drawer');
const scrim = document.querySelector('.cd-scrim');
const root = document.documentElement;
let lastFocus = null;

function endpoint(name) {
  return (TC.wcAjax || '/?wc-ajax=%%endpoint%%').replace('%%endpoint%%', name);
}

function open() {
  if (!drawer) return;
  lastFocus = document.activeElement;
  drawer.hidden = false;
  scrim.hidden = false;
  void drawer.offsetWidth; // Leave display:none before the class change so the slide-in transition runs.
  requestAnimationFrame(() => {
    root.classList.add('cd-open');
    drawer.focus();
  });
}

function close() {
  if (!drawer || !root.classList.contains('cd-open')) return;
  root.classList.remove('cd-open');
  const done = () => { drawer.hidden = true; scrim.hidden = true; };
  drawer.addEventListener('transitionend', done, { once: true });
  setTimeout(done, 600); // In case transitions are disabled.
  if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus();
}

function bumpBadges() {
  document.querySelectorAll('.tc-cart-count.cd-badge').forEach((el) => {
    el.classList.remove('cd-bump');
    void el.offsetWidth;
    el.classList.add('cd-bump');
  });
}

/** Replace each fragment selector's elements with the new HTML. */
function applyFragments(fragments) {
  if (!fragments) return;
  Object.entries(fragments).forEach(([selector, html]) => {
    document.querySelectorAll(selector).forEach((el) => { el.outerHTML = html; });
  });
}

/** Mirror cart-fragments.js storage so the next page load does not show stale data. */
function storeFragments(data) {
  const p = window.wc_cart_fragments_params;
  if (!p || !data || !data.fragments) return;
  try {
    sessionStorage.setItem(p.fragment_name, JSON.stringify(data.fragments));
    sessionStorage.setItem(p.cart_hash_key, data.cart_hash || '');
    localStorage.setItem(p.cart_hash_key, data.cart_hash || '');
  } catch (e) { /* storage unavailable: fragments refresh on next load instead */ }
}

function announce(msg) {
  const live = drawer && drawer.querySelector('[data-cd-live]');
  if (live) live.textContent = msg;
}

function showError(msg) {
  const body = drawer.querySelector('.tc-drawer__body');
  drawer.querySelector('.cd__err')?.remove();
  const p = document.createElement('p');
  p.className = 'cd__err';
  p.setAttribute('role', 'alert');
  p.textContent = msg || (TC.i18n && TC.i18n.updateFailed) || 'Something went wrong.';
  body.before(p);
}

async function post(action, params) {
  const body = drawer.querySelector('.tc-drawer__body');
  const nonce = body ? body.dataset.nonce : '';
  body && body.classList.add('is-busy');
  drawer.querySelector('.cd__err')?.remove();
  try {
    const res = await fetch(endpoint(action), {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({ ...params, nonce }),
    });
    const data = await res.json();
    if (data.error || !data.fragments) {
      body && body.classList.remove('is-busy');
      showError(data.message);
      return;
    }
    applyFragments(data.fragments);
    storeFragments(data);
    document.dispatchEvent(new CustomEvent('tc:cart:changed', { detail: data }));
    const count = document.querySelector('.tc-cart-count.cd-badge')?.dataset.count || '0';
    announce(`Bag updated. ${count} item${count === '1' ? '' : 's'}.`);
  } catch (e) {
    body && body.classList.remove('is-busy');
    showError();
  }
}

if (drawer) {
  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-open-cart]');
    if (opener) {
      e.preventDefault();
      open();
      return;
    }
    if (e.target.closest('[data-close-cart]')) {
      close();
      return;
    }
    const item = e.target.closest('.cd__item');
    if (!item || !drawer.contains(item)) return;
    const qtyBtn = e.target.closest('[data-qty]');
    if (qtyBtn) post('tc_cart_set_qty', { key: item.dataset.key, qty: qtyBtn.dataset.qty });
    else if (e.target.closest('[data-remove]')) post('tc_cart_remove', { key: item.dataset.key });
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') close();
    // Keep Tab inside the open drawer.
    if (e.key === 'Tab' && root.classList.contains('cd-open')) {
      const f = [...drawer.querySelectorAll('a[href],button:not([disabled]),input,select,[tabindex]:not([tabindex="-1"])')]
        .filter((el) => el.offsetParent !== null);
      if (!f.length) return;
      const first = f[0];
      const last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  });

  document.addEventListener('tc:cart:added', () => { bumpBadges(); open(); });
}

window.TCCart = { open, close };
